import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { FolderPlus, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { api } from "../../lib/api";

const VISIBILITIES = [
  { id: "public", label: "Pública" },
  { id: "internal", label: "Interna" },
  { id: "restricted", label: "Restringida" },
  { id: "private", label: "Privada" },
] as const;

type Visibility = (typeof VISIBILITIES)[number]["id"];

interface Props {
  trigger?: React.ReactNode;
}

/**
 * B1.1 — Nueva colección dialog.
 *
 * Cuatro campos: título (req), descripción, visibilidad, tags (csv).
 * POST /v2/collections → invalida la lista → navega a /collections/$slug.
 */
export function NewCollectionDialog({ trigger }: Props = {}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<Visibility>("internal");
  const [tagsRaw, setTagsRaw] = useState("");
  const navigate = useNavigate();
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: () => {
      const tags = tagsRaw
        .split(/[,\s]+/)
        .map((t) => t.trim().replace(/^#/, ""))
        .filter(Boolean);
      return api.createCollection({
        title: title.trim(),
        description: description.trim() || undefined,
        visibility,
        tags: tags.length > 0 ? tags : undefined,
      });
    },
    onSuccess: (result) => {
      toast.success("Colección creada", { description: title.trim() });
      void qc.invalidateQueries({ queryKey: ["collections"] });
      setOpen(false);
      // Reset
      setTitle("");
      setDescription("");
      setTagsRaw("");
      setVisibility("internal");
      // Navigate to the new collection
      void navigate({ to: "/collections/$slug", params: { slug: result.slug } });
    },
    onError: (err) => toast.error(`No se pudo crear: ${String(err)}`),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || create.isPending) return;
    create.mutate();
  }

  const triggerEl = trigger ?? (
    <Button variant="primary" size="sm" data-testid="new-collection-cta">
      <Plus size={13} /> Nueva colección
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{triggerEl}</DialogTrigger>
      <DialogContent data-testid="new-collection-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus size={18} /> Nueva colección
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-3" onSubmit={onSubmit}>
          <div>
            <label
              className="block text-[10px] uppercase tracking-wider text-(--text-tertiary) mb-1"
              htmlFor="new-coll-title"
            >
              Título *
            </label>
            <Input
              id="new-coll-title"
              data-testid="new-collection-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="NetBox onboarding Q3"
              autoFocus
              required
            />
          </div>
          <div>
            <label
              className="block text-[10px] uppercase tracking-wider text-(--text-tertiary) mb-1"
              htmlFor="new-coll-desc"
            >
              Descripción
            </label>
            <Input
              id="new-coll-desc"
              data-testid="new-collection-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Lo que un developer nuevo necesita para…"
            />
          </div>
          <fieldset>
            <legend className="block text-[10px] uppercase tracking-wider text-(--text-tertiary) mb-1">
              Visibilidad
            </legend>
            <div className="flex flex-wrap gap-1.5" data-testid="new-collection-visibility">
              {VISIBILITIES.map((v) => (
                <button
                  type="button"
                  key={v.id}
                  data-testid={`new-collection-visibility-${v.id}`}
                  onClick={() => setVisibility(v.id)}
                  className={`rounded-md border px-3 py-1.5 text-xs transition ${
                    visibility === v.id
                      ? "border-(--accent-500) bg-(--accent-50) text-(--accent-700)"
                      : "border-(--border-subtle) hover:bg-(--bg-hover)"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </fieldset>
          <div>
            <label
              className="block text-[10px] uppercase tracking-wider text-(--text-tertiary) mb-1"
              htmlFor="new-coll-tags"
            >
              Etiquetas{" "}
              <span className="text-(--text-tertiary)">
                (opcional, separadas por coma o espacio)
              </span>
            </label>
            <Input
              id="new-coll-tags"
              data-testid="new-collection-tags"
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              placeholder="onboarding netbox runbook"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              data-testid="new-collection-submit"
              disabled={!title.trim() || create.isPending}
            >
              {create.isPending ? "Creando…" : "Crear colección"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
